// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
// eslint-disable-next-line no-restricted-imports
import type { Locale } from '@core/internationalize/models/locale';
// eslint-disable-next-line no-restricted-imports
import { toLocaleFromString } from '@core/internationalize/models/locale';
import { formatDate } from '@core/time/formatters';
import { getLocale as getLocaleFromTime } from '@core/time/locale';
import type { Time } from '@core/time/time';
import {
  DateFormat,
  getFormatStringsForLocale,
  getInfoForLocale,
  TimeFormat,
} from './dateTimeUtils';
import { SupportedFormat } from './SupportedFormat';

const getTimeFormatForLocale = (locale: Locale): TimeFormat =>
  getInfoForLocale(locale).timeFormat;
const getDateFormatForLocale = (locale: Locale): DateFormat =>
  getInfoForLocale(locale).dateFormat;

type TimeType = Dayjs | Time;

const formatLocalizedDate = (
  date: TimeType,
  format: SupportedFormat,
  getLocale?: LocaleGetter,
) =>
  formatDate(
    date,
    getFormatStringsForLocale(toLocaleFromString(getLocaleOrDefault(getLocale)()))[
      format
    ],
  );

const getLocaleOrDefault = (getLocale?: LocaleGetter): LocaleGetter =>
  typeof getLocale === 'function' ? getLocale : getLocaleFromTime;

export const isTwelveHourTimeFormat = (getLocale?: LocaleGetter): boolean =>
  getTimeFormatForLocale(toLocaleFromString(getLocaleOrDefault(getLocale)())) ===
  TimeFormat.TwelveHour;
export const isTwentyFourHourTimeFormat = (getLocale?: LocaleGetter): boolean =>
  getTimeFormatForLocale(toLocaleFromString(getLocaleOrDefault(getLocale)())) ===
  TimeFormat.TwentyFourHour;
export const isMonthDayYearFormat = (getLocale?: LocaleGetter): boolean =>
  getDateFormatForLocale(toLocaleFromString(getLocaleOrDefault(getLocale)())) ===
  DateFormat.MonthDayYear;
export const isDayMonthYearFormat = (getLocale?: LocaleGetter): boolean =>
  getDateFormatForLocale(toLocaleFromString(getLocaleOrDefault(getLocale)())) ===
  DateFormat.DayMonthYear;
export const toLocalizedAbbreviatedLong = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.AbbreviatedLong, getLocale);
export const toLocalizedAbrevLongWithoutDay = (
  date: TimeType,
  getLocale?: LocaleGetter,
) => formatLocalizedDate(date, SupportedFormat.AbrevLongWithoutDay, getLocale);
export const toLocalizedSlashedDate = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.SlashedDate, getLocale);
export const toLocalizedSlashedDateLongYear = (
  date: TimeType,
  getLocale?: LocaleGetter,
) => formatLocalizedDate(date, SupportedFormat.SlashedDateLongYear, getLocale);
export const toLocalizedTwoDigitMonthSlashedDate = (
  date: TimeType,
  getLocale?: LocaleGetter,
) => formatLocalizedDate(date, SupportedFormat.TwoDigitMonthSlashedDate, getLocale);
export const toLocalizedDayNameMonthDate = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.DayNameMonthDate, getLocale);

export const toLocalizedDayNameShortMonthDate = (
  date: TimeType,
  getLocale?: LocaleGetter,
) => formatLocalizedDate(date, SupportedFormat.DayNameShortMonthDate, getLocale);

export const toLocalizedDayNameShort = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.DayNameShort, getLocale);
export const toLocalizedMonthNameShortDate = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.MonthNameShortDate, getLocale);
export const toLocalizedShortMonthDate = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.ShortMonthDate, getLocale);
export const toLocalizedDayFirstLetter = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.DayNameShort, getLocale)[0];
export const toLocalizedDayOfMonth = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.DayOfMonth, getLocale);
export const toLocalizedMonthYear = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.MonthYear, getLocale);
export const toLocalizedDayNameLongMonthSlashDay = (
  date: TimeType,
  getLocale?: LocaleGetter,
) => formatLocalizedDate(date, SupportedFormat.DayNameLongMonthSlashDay, getLocale);
export const toLocalizedMonthNameYear = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.MonthNameYear, getLocale);
export const toLocalizedTime = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.Time, getLocale);
export const toMonthNameShort = (date: TimeType, getLocale?: LocaleGetter) =>
  formatLocalizedDate(date, SupportedFormat.MonthNameShort, getLocale);

export type LocaleGetter = () => string;

// Re-export DateTimeInfo from dateTimeUtils for backwards compatibility
export type { DateTimeInfo } from './dateTimeUtils';
